import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border text-sm font-normal transition-transform transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-45 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "border-control bg-transparent text-ink hover:bg-panel",
        primary:
          "border-transparent bg-accent text-accent-ink hover:bg-accent/85 hover:text-accent-ink",
        outline: "border-control bg-panel text-ink hover:bg-panel-raised",
        ghost: "border-transparent bg-transparent text-muted hover:bg-panel hover:text-ink",
        danger: "border-transparent bg-transparent text-danger hover:bg-danger/10",
      },
      size: {
        default: "h-9 px-[15px] text-sm pointer-coarse:h-11",
        sm: "h-9 px-3 text-xs pointer-coarse:min-h-11 pointer-coarse:min-w-11",
        lg: "h-11 px-6 text-sm pointer-coarse:h-11",
        icon: "size-9 rounded-full p-0 pointer-coarse:size-11",
        "icon-sm": "size-8 rounded-full p-0 pointer-coarse:size-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  ),
);
Button.displayName = "Button";

export { Button, buttonVariants };
